/**
 * Superadmin API
 * Håndterer organisasjonsadministrasjon (kun for superadmin-brukere)
 */

import api from './client';
import { filnavnFraContentDisposition, type AuditEksport } from './adminAudit.api';
import type { Organisasjon, CreateOrganisasjonData, UpdateOrganisasjonData, OrgKontekst, AdminAuditLogg } from './types';

export const superadminAPI = {
  /**
   * Hent alle organisasjoner med statistikk
   */
  getOrganisasjoner: async (): Promise<{ organisasjoner: Organisasjon[] }> => {
    const response = await api.get('/superadmin/organisasjoner');
    return response.data;
  },

  /**
   * Hent én organisasjon med statistikk
   */
  getOrganisasjon: async (id: number): Promise<{ organisasjon: Organisasjon }> => {
    const response = await api.get(`/superadmin/organisasjoner/${id}`);
    return response.data;
  },

  /**
   * Opprett ny organisasjon og første admin-bruker
   */
  createOrganisasjon: async (data: CreateOrganisasjonData): Promise<{
    organisasjon: Organisasjon;
    adminUser: { id: number; firstName: string; lastName: string; email: string };
  }> => {
    const response = await api.post('/superadmin/organisasjoner', data);
    return response.data;
  },

  /**
   * Hent antall OAuth-koblinger for en organisasjon
   */
  getOAuthLinks: async (id: number): Promise<{
    googleCount: number;
    facebookCount: number;
  }> => {
    const response = await api.get(`/superadmin/organisasjoner/${id}/oauth-links`);
    return response.data;
  },

  /**
   * Oppdater organisasjon
   */
  updateOrganisasjon: async (id: number, data: UpdateOrganisasjonData): Promise<{
    organisasjon: Organisasjon;
    removedLinks: { google: number; facebook: number };
  }> => {
    const response = await api.put(`/superadmin/organisasjoner/${id}`, data);
    return response.data;
  },

  /**
   * Hent gjeldende org-kontekst (null hvis ingen er valgt)
   */
  getKontekst: async (): Promise<{ orgKontekst: OrgKontekst | null }> =>
    (await api.get('/superadmin/kontekst')).data,

  /**
   * Velg organisasjon å jobbe i (også inaktiv). Serveren setter signert cookie.
   */
  velgKontekst: async (organisasjonId: number): Promise<{ orgKontekst: OrgKontekst }> =>
    (await api.post('/superadmin/kontekst', { organisasjonId })).data,

  /**
   * Avslutt org-konteksten
   */
  avsluttKontekst: async (): Promise<{ orgKontekst: null }> =>
    (await api.delete('/superadmin/kontekst')).data,

  /**
   * Plattformloggen (hendelser uten organisasjon)
   */
  getPlattformAuditLogg: async (params: Record<string, unknown>): Promise<{ auditLogg: AdminAuditLogg[] }> =>
    (await api.get('/superadmin/audit-logg', { params })).data,

  /**
   * Last ned plattformloggen som JSON-fil (periode + filtre)
   */
  eksporterPlattformAuditLogg: async (params: Record<string, unknown>): Promise<AuditEksport> => {
    const response = await api.get('/superadmin/audit-logg/eksport', { params, responseType: 'blob' });
    return {
      data: response.data,
      filnavn: filnavnFraContentDisposition(response.headers?.['content-disposition']),
    };
  },
};
